/**
 * Модель прогресса, общая для клиента и сервера: одни и те же правила
 * нормализации и слияния и в localStorage, и в MongoDB.
 */

export interface DayStat {
  seconds: number
  answers: number
}

export interface Progress {
  /** `"7x8"` → уровень владения 0–5. */
  mastery: Record<string, number>
  xp: number
  streak: number
  /** День последней игры — для стрика. */
  lastPlayed: string | null
  history: Record<string, DayStat>
}

export const MASTERY_MAX = 5
/** Таблица до 10×10. */
export const MAX_FACTOR = 10
/** Всего фактов: 2..10 × 2..10. */
export const FACT_TOTAL = (MAX_FACTOR - 1) * (MAX_FACTOR - 1)
/** Сколько XP в одном уровне. */
export const XP_PER_LEVEL = 100

/** Больше года истории ни ребёнку, ни родителю не нужно, а документ растёт. */
const MAX_HISTORY_DAYS = 400

export const factKey = (a: number, b: number): string => `${a}x${b}`

export const factLabel = (key: string): string => key.replace('x', ' × ')

export const EMPTY_PROGRESS: Progress = {
  mastery: {},
  xp: 0,
  streak: 0,
  lastPlayed: null,
  history: {},
}

export function emptyProgress(): Progress {
  return { mastery: {}, xp: 0, streak: 0, lastPlayed: null, history: {} }
}

function isFactKey(key: string): boolean {
  const match = /^(\d+)x(\d+)$/.exec(key)
  if (!match) return false
  const a = Number(match[1])
  const b = Number(match[2])
  return a >= 2 && a <= MAX_FACTOR && b >= 2 && b <= MAX_FACTOR
}

function isDayKey(key: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(key)
}

function clampInt(value: unknown, min: number, max: number): number {
  const n = Number(value)
  if (!Number.isFinite(n)) return min
  return Math.min(max, Math.max(min, Math.round(n)))
}

/**
 * Приводит что угодно к валидному прогрессу: мусорные ключи и значения
 * отбрасываются. Одна функция и для чтения localStorage, и для тела запроса.
 */
export function coerceProgress(raw: unknown): Progress {
  const p = (raw ?? {}) as Partial<Progress>
  const mastery: Record<string, number> = {}
  if (p.mastery && typeof p.mastery === 'object') {
    for (const [key, value] of Object.entries(p.mastery)) {
      if (isFactKey(key)) {
        mastery[key] = clampInt(value, 0, MASTERY_MAX)
      }
    }
  }

  const history: Record<string, DayStat> = {}
  if (p.history && typeof p.history === 'object') {
    const days = Object.keys(p.history).filter(isDayKey).sort().slice(-MAX_HISTORY_DAYS)
    for (const day of days) {
      const stat = (p.history as Record<string, Partial<DayStat>>)[day]
      history[day] = {
        seconds: clampInt(stat?.seconds, 0, 86_400),
        answers: clampInt(stat?.answers, 0, 100_000),
      }
    }
  }

  return {
    mastery,
    xp: clampInt(p.xp, 0, 10_000_000),
    streak: clampInt(p.streak, 0, 10_000),
    lastPlayed: typeof p.lastPlayed === 'string' && isDayKey(p.lastPlayed) ? p.lastPlayed : null,
    history,
  }
}

/**
 * Слияние двух копий прогресса — берём лучшее по каждому полю. Идемпотентно:
 * клиент шлёт полный снапшот, поэтому повторная отправка ничего не задваивает.
 */
export function mergeProgress(a: Progress, b: Progress): Progress {
  const mastery: Record<string, number> = { ...a.mastery }
  for (const [key, value] of Object.entries(b.mastery)) {
    mastery[key] = Math.max(mastery[key] ?? 0, value)
  }

  const history: Record<string, DayStat> = { ...a.history }
  for (const [day, stat] of Object.entries(b.history)) {
    const cur = history[day]
    history[day] = cur
      ? { seconds: Math.max(cur.seconds, stat.seconds), answers: Math.max(cur.answers, stat.answers) }
      : stat
  }

  // Стрик берём у той копии, что играла позже: прерванный стрик не должен
  // «воскресать» из старой записи с большим числом.
  const lastPlayed = pickLater(a.lastPlayed, b.lastPlayed)
  const streak =
    a.lastPlayed === b.lastPlayed
      ? Math.max(a.streak, b.streak)
      : lastPlayed === a.lastPlayed
        ? a.streak
        : b.streak

  return {
    mastery,
    xp: Math.max(a.xp, b.xp),
    streak,
    lastPlayed,
    history,
  }
}

function pickLater(a: string | null, b: string | null): string | null {
  if (!a) return b
  if (!b) return a
  return a >= b ? a : b
}

/** Доля таблицы, которая освоена, в процентах. */
export function masteredPct(progress: Progress): number {
  let sum = 0
  for (let a = 2; a <= MAX_FACTOR; a++) {
    for (let b = 2; b <= MAX_FACTOR; b++) {
      sum += progress.mastery[factKey(a, b)] ?? 0
    }
  }
  return Math.round((sum / (FACT_TOTAL * MASTERY_MAX)) * 100)
}

/** Сколько фактов доведено до максимального уровня владения. */
export function masteredFacts(progress: Progress): number {
  return Object.values(progress.mastery).filter((v) => v >= MASTERY_MAX).length
}

export function level(progress: Progress): number {
  return Math.floor(progress.xp / XP_PER_LEVEL) + 1
}

export function xpInLevel(progress: Progress): number {
  return progress.xp % XP_PER_LEVEL
}

export function totalAnswers(progress: Progress): number {
  return Object.values(progress.history).reduce((sum, day) => sum + day.answers, 0)
}

/** Освоенность одной таблицы (n × 2..10) в процентах. */
export function tablePct(progress: Progress, n: number): number {
  let sum = 0
  for (let b = 2; b <= MAX_FACTOR; b++) {
    sum += progress.mastery[factKey(n, b)] ?? 0
  }
  return Math.round((sum / ((MAX_FACTOR - 1) * MASTERY_MAX)) * 100)
}

/**
 * Зона таблицы, которую сейчас тренируем. Дети приходят на разной стадии:
 * кто-то знает только до 5, кто-то учит одну строчку.
 */
export type ScopeKind = 'all' | 'upto' | 'single'

export interface Scope {
  kind: ScopeKind
  n: number
}

export const DEFAULT_SCOPE: Scope = { kind: 'all', n: MAX_FACTOR }
/** «До 2» — это один пример 2×2, играть нечем. */
export const MIN_UPTO = 3
/** Порог «выучено» — общий для медалей, наклеек и событий ленты. */
export const DONE_PCT = 90

export function coerceScope(raw: unknown): Scope {
  const s = (raw ?? {}) as Partial<Scope>
  const kind: ScopeKind = s.kind === 'upto' || s.kind === 'single' ? s.kind : 'all'
  if (kind === 'all') return { kind, n: MAX_FACTOR }

  const n = clampInt(s.n, kind === 'upto' ? MIN_UPTO : 2, MAX_FACTOR)

  return { kind, n }
}

/**
 * Факты зоны. `single` даёт только `n × b`: в `mastery` ключи `7x8` и `8x7`
 * разные, и медаль таблицы считает `tablePct`, который смотрит лишь `n × b`.
 * Разреши мы обе перестановки — половина ответов уходила бы в чужую таблицу.
 */
export function scopeFacts(scope: Scope): Array<[number, number]> {
  const facts: Array<[number, number]> = []

  if (scope.kind === 'single') {
    for (let b = 2; b <= MAX_FACTOR; b++) facts.push([scope.n, b])
    return facts
  }

  const top = scope.kind === 'upto' ? scope.n : MAX_FACTOR
  for (let a = 2; a <= top; a++) {
    for (let b = 2; b <= top; b++) facts.push([a, b])
  }

  return facts
}

/** Освоенность зоны в процентах — то, что показывает кольцо на главном. */
export function scopePct(progress: Progress, scope: Scope): number {
  const facts = scopeFacts(scope)
  if (facts.length === 0) return 0

  let sum = 0
  for (const [a, b] of facts) {
    sum += progress.mastery[factKey(a, b)] ?? 0
  }

  return Math.round((sum / (facts.length * MASTERY_MAX)) * 100)
}

/** Наибольший рубеж «выучил всё до N». 0 — даже квадрат 2×2 ещё не готов. */
export function zoneUpto(progress: Progress): number {
  let best = 0
  for (let n = 2; n <= MAX_FACTOR; n++) {
    if (scopePct(progress, { kind: 'upto', n }) < DONE_PCT) break
    best = n
  }

  return best
}
